import { useCallback, useEffect, useState } from 'react';

/**
 * Minimal dependency-free hash router (ported from the portal). The route is the bare
 * path after `#/` matched against the studio's known views; anything unknown falls back.
 * Parsing is a pure function so it is unit-testable without a DOM (CONVENTIONS:
 * deterministic logic is pure).
 */
export function parseHash<T extends string>(hash: string, views: readonly T[], fallback: T): T {
  const raw = (hash.replace(/^#\/?/, '').split('?')[0] ?? '').trim();
  return (views as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

export function hashFor<T extends string>(view: T): string {
  return `#/${view}`;
}

/** React binding over `parseHash`; re-renders on `hashchange`. */
export function useHashRoute<T extends string>(
  views: readonly T[],
  fallback: T,
): [T, (view: T) => void] {
  const [route, setRoute] = useState<T>(() => parseHash(window.location.hash, views, fallback));

  useEffect(() => {
    const onChange = (): void => setRoute(parseHash(window.location.hash, views, fallback));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, [views, fallback]);

  const navigate = useCallback((view: T) => {
    window.location.hash = hashFor(view);
  }, []);

  return [route, navigate];
}
